import * as Y from 'yjs';

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}

/**
 * Replaces a Y.Text's contents with `next` using the smallest possible edit.
 *
 * The naive implementation — delete everything, insert the new string — would destroy
 * concurrent edits: a teammate's simultaneous change to the same paragraph would be
 * deleted along with the old text and never merge. Applying only the changed middle
 * section preserves the surrounding characters, so Yjs can interleave both edits.
 */
export function applyTextDiff(text: Y.Text, next: string): void {
  const current = text.toString();
  if (current === next) return;

  const max = Math.min(current.length, next.length);
  let start = 0;
  while (start < max && current.charCodeAt(start) === next.charCodeAt(start)) start += 1;

  let endCurrent = current.length;
  let endNext = next.length;
  while (
    endCurrent > start &&
    endNext > start &&
    current.charCodeAt(endCurrent - 1) === next.charCodeAt(endNext - 1)
  ) {
    endCurrent -= 1;
    endNext -= 1;
  }

  // Yjs indexes by UTF-16 code unit, so a boundary that lands between a high and a low
  // surrogate would split an emoji into two invalid halves. Widen the edit by one unit.
  if (start > 0 && isLowSurrogate(current.charCodeAt(start))) start -= 1;
  if (endCurrent < current.length && isLowSurrogate(current.charCodeAt(endCurrent))) {
    endCurrent += 1;
    endNext += 1;
  }

  if (endCurrent > start) text.delete(start, endCurrent - start);
  if (endNext > start) text.insert(start, next.slice(start, endNext));
}
